import React, {ComponentProps, useCallback, useEffect, useMemo, useState} from 'react';
import AbstractDeclarantStore from "../../stores/AbstractDeclarantStore";
import ActionButton from "./groupButton";
import {Nullable} from "../../models/Common";
import { Moment as MomentType} from "moment";
import Authorize from "../Authorize";
import {UPLOAD_XML} from "../../constants/authorize";
import {PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {DeclarantTwsSyncResponseAny} from "../../models/DeclarantTwsSync";
import TwsSyncRangeWarningModal from "./twsSyncRangeWarningModal";
import {ModalHandler, useExecuteModal} from "../../hooks/useExecuteModal";

type TwsSyncButtonProps = {
  store: AbstractDeclarantStore,
  existingOnly?: boolean,
  onSuccess?: () => void,
  disabled?: boolean,
};

type TwsSyncDateRange = {
  date_from: string,
  date_to: string,
};

const TwsSyncButton = ({ store, existingOnly, onSuccess }: TwsSyncButtonProps) => {
  const warningModalHandler: ModalHandler = useExecuteModal();
  const [syncLoading, setSyncLoading] = useState<boolean>(false);

  const isDateRangeAvailable: boolean = useMemo((): boolean => {
    const startDate = store.parameters['start_date'];
    const endDate = store.parameters['end_date'];

    if(!startDate || !endDate) {
      return false;
    }

    return true;
  }, [store.parameters]);

  const dateParams: Nullable<TwsSyncDateRange> = useMemo(() => {
    if(!isDateRangeAvailable) {
      return null;
    }

    return {
      date_from: (store.parameters['start_date'] as MomentType).format('YYYY-MM-DD'),
      date_to: (store.parameters['end_date'] as MomentType).format('YYYY-MM-DD'),
    };
  }, [store.parameters, isDateRangeAvailable]);

  const handleSync = useCallback(async (): Promise<void> => {
    try {
      setSyncLoading(true);
      if(dateParams) {
        const result: DeclarantTwsSyncResponseAny = await store.twsSync({...dateParams}, existingOnly);
        store.load({}, true);
        if(result.success && onSuccess) {
          onSuccess();
        }
      }
    } finally {
      setSyncLoading(false);
    }
  }, [dateParams, existingOnly, store, onSuccess])

  const tooltipData: Pick<ComponentProps<typeof ActionButton>, 'tooltip' | 'tooltipProps'> = useMemo(() => {
    if(isDateRangeAvailable) {
      return {};
    } else {
      return {
        tooltip: true,
        tooltipProps: {
          title: 'Please select date range',
          placement: "top",
        },
      };
    }
  }, [isDateRangeAvailable]);

  const TwsActionButton = ({ disabled }: Pick<TwsSyncButtonProps, 'disabled'>) => {
    return (
      <ActionButton
        disabled={disabled || !isDateRangeAvailable}
        onClick={async () => {
          if(isDateRangeAvailable) {
            const start = store.parameters['start_date'] as MomentType;
            const end = store.parameters['end_date'] as MomentType;

            console.log(end.diff(start, 'days', true));
            if(end.diff(start, 'days', true) > 2) {
              warningModalHandler.executeModal();
            } else {
              await handleSync();
            }
          }
        }}
        text={existingOnly ? "Rewrite" : "Get New"}
        loading={syncLoading}
        content={existingOnly ? <ReloadOutlined /> : <PlusOutlined />}
        {...tooltipData}
      />
    );
  };

  return (
    <Authorize deniedComponent={<TwsActionButton disabled />} permission={UPLOAD_XML}>
      <>
        <TwsActionButton />
        <TwsSyncRangeWarningModal onApply={handleSync} handler={warningModalHandler} />
      </>
    </Authorize>
  );
};

export default TwsSyncButton;